<template>
  <div class="timeline-wrapper" style="width: 95%">
    <div class="timeline-row">
      <div v-for="(block, i) in computedBlocks" :key="i" class="period-block" :style="{ left: block.leftPct + '%', width: block.widthPct + '%', background: block.color }" @mousemove="onMouseMove($event, block)" @mouseleave="tooltipVisible = false">
        <span v-if="block.widthPct > 4">{{ block.days }}d</span>
      </div>
    </div>

    <div class="timeline-axis">
      <div v-for="tick in axisTicks" :key="tick.day" class="axis-tick" :style="{ left: tick.pct + '%' }">{{ tick.label }}</div>
    </div>

    <div class="timeline-legend">
      <div v-for="(color, id) in companyColors" :key="id" class="legend-item">
        <span class="legend-dot" :style="{ background: color }"></span>
        {{ $t("labels.client") }} {{ company_by_id(id).company_name }} &mdash; {{ companyTotalDays[id] }} {{ $t("labels.days") }} ({{ companyWorkedDays[id] || 0 }} {{ $t("labels.daysworkedfor") }})
        <span v-if="warningCompanyIds.has(Number(id))" class="legend-warning" title="8-maanden signalering">&#9888;</span>
      </div>
    </div>

    <div v-if="eightMonthAlerts.length > 0" class="eight-month-alerts">
      <div class="eight-month-title">&#9888; {{ $t("labels.eightMonthRule") }}</div>
      <div v-for="(alert, i) in eightMonthAlerts" :key="'alert-' + i" :class="['alert-item', alert.inCooldown ? 'alert-item--danger' : 'alert-item--warning']">
        <strong>{{ company_by_id(alert.companyId).company_name }}</strong>
        &mdash; {{ alert.months }} {{ $t("labels.eightMonthWorked") }}
        <span class="alert-dates">({{ formatDateObj(alert.runStart) }} &ndash; {{ formatDateObj(alert.runEnd) }})</span>
        <span v-if="alert.inCooldown" class="cooldown-badge cooldown-badge--active"> {{ $t("labels.cooldownUntil") }} {{ formatDateObj(alert.cooldownEnd) }} </span>
        <span v-else class="cooldown-badge cooldown-badge--expired"> {{ $t("labels.cooldownExpiredOn") }} {{ formatDateObj(alert.cooldownEnd) }} </span>
      </div>
    </div>

    <div v-if="tooltipVisible" class="period-tooltip" :style="{ right: tooltipX + 'px', top: tooltipY + 'px' }">
      <strong>{{ $t("labels.client") }} {{ company_by_id(tooltipData.companyId).company_name }}</strong
      ><br />
      {{ tooltipData.start }} &rarr; {{ tooltipData.end }}<br />
      <small>{{ tooltipData.days }} {{ $t("labels.days") }} &mdash; {{ tooltipData.workedDays }} {{ $t("labels.daysworkedfor") }}</small>
    </div>
  </div>
</template>

<script>
const PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

export default {
  name: "WorkperiodTimeline",

  props: {
    // Array of { startdate: 'YYYY-MM-DD', enddate: 'YYYY-MM-DD', company_id: Number, ... }
    periods: {
      type: Array,
      required: true,
    },
    totalDays: {
      type: Number,
      default: 365,
    },
  },

  data() {
    return {
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipData: {},
    };
  },

  computed: {
    day0() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    },

    companyColors() {
      const map = {};
      let idx = 0;
      this.periods.forEach((p) => {
        if (!map[p.company_id]) map[p.company_id] = PALETTE[idx++ % PALETTE.length];
      });
      return map;
    },

    companyTotalDays() {
      const totals = {};
      this.periods.forEach((p) => {
        const days = this.daysBetween(this.parseDate(p.startdate), this.parseDate(p.enddate)) + 1;
        totals[p.company_id] = (totals[p.company_id] || 0) + days;
      });
      return totals;
    },

    companyWorkedDays() {
      const totals = {};
      this.periods.forEach((p) => {
        totals[p.company_id] = (totals[p.company_id] || 0) + (p.total_hours_entries || 0);
      });
      return totals;
    },

    computedBlocks() {
      return this.periods.map((p) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const originalStart = this.parseDate(p.startdate);
        const start = originalStart < this.day0 ? this.day0 : originalStart;
        const originalEnd = this.parseDate(p.enddate);
        const end = originalEnd > today ? today : originalEnd;
        const startDay = this.daysBetween(this.day0, start);
        const days = this.daysBetween(start, end) + 1;
        return {
          leftPct: (startDay / this.totalDays) * 100,
          widthPct: (days / this.totalDays) * 100,
          color: this.companyColors[p.company_id],
          companyId: p.company_id,
          startDay,
          endDay: startDay + days,
          days,
          workedDays: p.total_hours_entries || 0,
          start: this.formatDate(p.startdate),
          end: this.formatDate(p.enddate),
        };
      });
    },

    eightMonthAlerts() {
      const EIGHT_MONTHS_DAYS = 244;
      const FOUR_MONTHS_DAYS = 122;
      const result = [];
      const companies = [...new Set(this.periods.map((p) => p.company_id))];

      companies.forEach((companyId) => {
        const companyPeriods = [...this.periods].filter((p) => p.company_id == companyId).sort((a, b) => this.parseDate(a.startdate) - this.parseDate(b.startdate));

        if (companyPeriods.length === 0) return;

        const otherPeriods = this.periods.filter((p) => p.company_id != companyId);

        // Group into uninterrupted runs (no other company appears in the gap)
        const runs = [];
        let currentRun = [companyPeriods[0]];

        for (let i = 1; i < companyPeriods.length; i++) {
          const prevEnd = this.parseDate(companyPeriods[i - 1].enddate);
          const currStart = this.parseDate(companyPeriods[i].startdate);
          const gapDays = this.daysBetween(prevEnd, currStart);
          const interrupted =
            gapDays >= FOUR_MONTHS_DAYS ||
            otherPeriods.some((op) => {
              const opStart = this.parseDate(op.startdate);
              const opEnd = this.parseDate(op.enddate);
              return opEnd > prevEnd && opStart < currStart;
            });

          if (interrupted) {
            runs.push(currentRun);
            currentRun = [companyPeriods[i]];
          } else {
            currentRun.push(companyPeriods[i]);
          }
        }
        runs.push(currentRun);

        runs.forEach((run) => {
          const runStart = this.parseDate(run[0].startdate);
          const runEnd = this.parseDate(run[run.length - 1].enddate);
          const days = this.daysBetween(runStart, runEnd) + 1;

          if (days >= EIGHT_MONTHS_DAYS) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const cooldownEnd = new Date(runEnd.getTime() + FOUR_MONTHS_DAYS * 86400000);
            console.log(today, cooldownEnd);
            if (today < cooldownEnd) {
              result.push({
                companyId,
                runStart,
                runEnd,
                days,
                months: Math.round(days / 30.44),
                cooldownEnd,
                inCooldown: today < cooldownEnd,
              });
            }
          }
        });
      });

      return result;
    },

    warningCompanyIds() {
      return new Set(this.eightMonthAlerts.map((a) => a.companyId));
    },

    axisTicks() {
      const ticks = [];
      for (let i = 0; i <= 13; i++) {
        const day = Math.round((i / 13) * this.totalDays);
        const date = new Date(this.day0.getTime() + day * 86400000);
        ticks.push({
          day,
          pct: ((day / this.totalDays) * 100).toFixed(2),
          label: date.toLocaleDateString("nl-NL", { day: "numeric", month: "short" }),
        });
      }
      return ticks;
    },
  },

  methods: {
    parseDate(str) {
      const [y, m, d] = str.split("-").map(Number);
      return new Date(y, m - 1, d);
    },

    daysBetween(a, b) {
      return Math.round((b - a) / 86400000);
    },

    formatDate(str) {
      return this.parseDate(str).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
    },

    formatDateObj(date) {
      return date.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
    },

    onMouseMove(e, block) {
      const rect = this.$el.getBoundingClientRect();
      this.tooltipX = rect.right - e.clientX + 12;
      this.tooltipY = e.clientY - rect.top - 10;
      this.tooltipData = block;
      this.tooltipVisible = true;
    },
  },
};
</script>

<style scoped>
.timeline-wrapper {
  position: relative;
}

.timeline-row {
  position: relative;
  height: 36px;
  background: #e9ecef;
  border-radius: 4px;
}

.period-block {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  cursor: default;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  transition: filter 0.15s;
}
.period-block:hover {
  filter: brightness(1.15);
  z-index: 10;
}

.timeline-axis {
  position: relative;
  height: 20px;
  margin-top: 2px;
}
.axis-tick {
  position: absolute;
  transform: translateX(-50%);
  font-size: 10px;
  color: #888;
  white-space: nowrap;
}

.timeline-legend {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-warning {
  color: #d97706;
  font-size: 13px;
  margin-left: 4px;
}

.eight-month-alerts {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.eight-month-title {
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

.alert-item {
  padding: 7px 12px;
  border-radius: 4px;
  font-size: 12px;
  border-left: 4px solid;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.alert-item--danger {
  background: #fee2e2;
  border-color: #ef4444;
  color: #7f1d1d;
}

.alert-item--warning {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #78350f;
}

.alert-dates {
  color: inherit;
  opacity: 0.75;
  font-size: 11px;
}

.cooldown-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
}

.cooldown-badge--active {
  background: #ef4444;
  color: #fff;
}

.cooldown-badge--expired {
  background: #d97706;
  color: #fff;
}

.period-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 100;
  line-height: 1.6;
  white-space: nowrap;
}
</style>
